// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module useRealtimeStats
 * @package @geenius/analytics/react
 * @description Polls realtime analytics data for the React variant. The hook
 * manages loading state and interval cleanup for dashboard widgets that need
 * continuously refreshed traffic summaries.
 */

import {
  AnalyticsProviderError,
  DEFAULT_REALTIME_INTERVAL,
  type RealtimeStats,
} from "@geenius/analytics";
import { useEffect, useState } from "react";

/**
 * Realtime polling state returned by {@link useRealtimeStats}.
 *
 * @property stats The latest successfully fetched realtime statistics.
 * @property isLoading Indicates whether the first fetch is still in flight.
 * @property error Latest typed polling error, cleared after the next successful fetch.
 */
export interface UseRealtimeStatsReturn {
  stats: RealtimeStats | null;
  isLoading: boolean;
  error: AnalyticsProviderError | null;
}

function normalizeRealtimeStatsError(err: unknown): AnalyticsProviderError {
  if (err instanceof AnalyticsProviderError) {
    return err;
  }

  const message =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : "Realtime stats polling failed";

  return new AnalyticsProviderError({
    message,
    ...(err instanceof Error ? { cause: err } : {}),
    context: {
      source: "useRealtimeStats",
    },
  });
}

/**
 * Polls realtime analytics statistics on a fixed interval.
 *
 * @param fetcher Async function that returns the current realtime statistics snapshot.
 * @param interval Poll interval in milliseconds. Defaults to the shared realtime interval.
 * @returns The latest realtime stats, loading state, and normalized polling error.
 */
export function useRealtimeStats(
  fetcher: () => Promise<RealtimeStats>,
  interval = DEFAULT_REALTIME_INTERVAL,
): UseRealtimeStatsReturn {
  const [stats, setStats] = useState<RealtimeStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AnalyticsProviderError | null>(null);

  useEffect(() => {
    let active = true;

    const poll = async (): Promise<void> => {
      try {
        const nextStats = await fetcher();
        if (active) {
          setStats(nextStats);
          setError(null);
        }
      } catch (err) {
        if (active) {
          setError(normalizeRealtimeStatsError(err));
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    void poll();
    const id = setInterval(() => {
      void poll();
    }, interval);

    return () => {
      active = false;
      clearInterval(id);
    };
  }, [fetcher, interval]);

  return { stats, isLoading, error };
}
